import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import {
  Users, Plus, Trash2, Loader2, Check, ArrowRight, Copy,
  Clock, Bell, Share2, PartyPopper, Home, AlertTriangle, Sparkles
} from "lucide-react";
import PaymentTrustBadge from "../components/PaymentTrustBadge";
import { isCustomerLoggedIn } from "../utils/customerAuth";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════════
   SplitPaySetup — host creates a pool (choose total, add friends)
════════════════════════════════════════════════════════════════ */
export const SplitPaySetup = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { requireAuth } = useCustomerAuth();
  const [form, setForm] = useState({
    host_name: "",
    host_phone: "",
    total_amount: parseInt(params.get("budget") || "6000"),
    occasion: params.get("occasion") || "Birthday",
    city: params.get("city") || "Mumbai",
    event_date: "",
    event_time: "19:00",
    include_host: true,
  });
  const [friends, setFriends] = useState([
    { id: "f-1", name: "", phone: "" }, { id: "f-2", name: "", phone: "" }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const initFriends = parseInt(params.get("friends") || "4");
    // Friends = total minus host (host counts as 1)
    const need = Math.max(2, initFriends - (form.include_host ? 1 : 0));
    setFriends(Array.from({ length: need }, (_, i) => ({ id: `f-${Date.now()}-${i}`, name: "", phone: "" })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalContribs = friends.length + (form.include_host ? 1 : 0);
  const perHead = Math.round(form.total_amount / totalContribs);

  const addFriend = () => setFriends([...friends, { id: `f-${Date.now()}-${friends.length}`, name: "", phone: "" }]);
  const removeFriend = (i) => setFriends(friends.filter((_, j) => j !== i));
  const updateFriend = (i, k, v) => {
    const c = [...friends]; c[i] = { ...c[i], [k]: v }; setFriends(c);
  };

  const create = async () => {
    setError("");
    // Centralised login gate — customers must be signed in to start a split pool.
    if (!isCustomerLoggedIn()) { requireAuth(() => create()); return; }
    if (!form.host_name.trim() || !form.host_phone.trim()) {
      setError("Enter your name and phone"); return;
    }
    if (friends.some(f => !f.name.trim() || !f.phone.trim())) {
      setError("All friends must have name and phone"); return;
    }
    if (friends.length < 2) { setError("Minimum 2 friends required"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/party-vibes/split-pay/create`, {
        ...form,
        friends,
        plan_id: params.get("plan_id") || null,
        deadline_hours: 48,
      });
      navigate(`/pool/${data.pool_id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create pool");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="split-pay-setup">
      <div className="bg-gradient-to-br from-[#1E1B4B] to-[#E83E8C] px-5 py-8 text-white">
        <button onClick={() => navigate(-1)} className="text-white/70 text-xs mb-3 flex items-center gap-1"><Home size={12}/> Back</button>
        <h1 className="text-3xl font-black font-['Outfit']">Split the vibe 💸</h1>
        <p className="text-sm opacity-80 mt-1">Add your squad · each pays their share · booking unlocks when pool hits 100%</p>
      </div>

      <div className="max-w-xl mx-auto p-5 space-y-4">
        {/* Budget summary */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border-2 border-[#E83E8C]/10">
          <div className="flex justify-between items-baseline">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Per-head share</p>
              <p className="text-3xl font-black text-[#1E1B4B]">₹{perHead.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Total</p>
              <p className="text-lg font-bold text-[#1E1B4B]">₹{form.total_amount.toLocaleString()}</p>
              <p className="text-[10px] text-gray-400">{totalContribs} contributors</p>
            </div>
          </div>
        </div>

        {/* Host details */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">You (the host)</h3>
          <input placeholder="Your name" value={form.host_name} onChange={e => setForm({ ...form, host_name: e.target.value })} className="w-full mb-2 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="host-name-input"/>
          <input placeholder="Your WhatsApp" value={form.host_phone} onChange={e => setForm({ ...form, host_phone: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="host-phone-input"/>
          <label className="flex items-center gap-2 mt-3 text-xs text-gray-600">
            <input type="checkbox" checked={form.include_host} onChange={e => setForm({ ...form, include_host: e.target.checked })} className="accent-[#E83E8C]" data-testid="include-host-checkbox"/>
            Include my share in the pool
          </label>
        </div>

        {/* Event date/time */}
        <div className="bg-white rounded-2xl p-4 shadow-sm grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-1 block">Event Date</label>
            <input type="date" value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="event-date"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-1 block">Time</label>
            <input type="time" value={form.event_time} onChange={e => setForm({ ...form, event_time: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"/>
          </div>
        </div>

        {/* Friends list */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3">Your squad ({friends.length})</h3>
          <div className="space-y-2">
            {friends.map((f, i) => (
              <div key={f.id} className="flex gap-2 items-center" data-testid={`friend-row-${i}`}>
                <input placeholder="Name" value={f.name} onChange={e => updateFriend(i, "name", e.target.value)} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid={`friend-name-${i}`}/>
                <input placeholder="Phone" value={f.phone} onChange={e => updateFriend(i, "phone", e.target.value)} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid={`friend-phone-${i}`}/>
                {friends.length > 2 && (
                  <button onClick={() => removeFriend(i)} className="text-gray-400 hover:text-red-500" data-testid={`friend-remove-${i}`}>
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            ))}
          </div>
          <button onClick={addFriend} className="mt-3 w-full py-2 rounded-xl border-2 border-dashed border-gray-200 text-xs text-gray-500 hover:border-[#E83E8C] hover:text-[#E83E8C] transition flex items-center justify-center gap-1" data-testid="add-friend-btn">
            <Plus size={14}/> Add another friend
          </button>
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <button onClick={create} disabled={loading} className="w-full py-4 rounded-xl bg-gradient-to-r from-[#E83E8C] to-orange-500 text-white font-bold flex items-center justify-center gap-2 shadow-lg disabled:opacity-50" data-testid="create-pool-btn">
          {loading ? <><Loader2 className="animate-spin" size={16}/> Creating…</> : <><Users size={16}/> Send WhatsApp Pay Links <ArrowRight size={16}/></>}
        </button>
        <p className="text-[10px] text-gray-400 text-center">Each friend gets a personal Razorpay link · host deadline 48h</p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   PoolTracker — live pool status for host (or anyone with link)
════════════════════════════════════════════════════════════════ */
export const PoolTracker = () => {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(false);
  const [copied, setCopied] = useState("");
  const [recentlyPaid, setRecentlyPaid] = useState(null);   // { friendId, name, until }
  const [confetti, setConfetti] = useState([]);              // visible confetti bursts
  const prevPaidRef = React.useRef(new Set());

  const fetchPool = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/party-vibes/split-pay/${poolId}`);
      setPool(prev => {
        // Detect new payments since last poll
        const newlyPaid = (data.friends || []).filter(f =>
          f.status === "paid" && !prevPaidRef.current.has(f.friend_id)
        );
        // Skip animation on first load (seed prev set without firing)
        if (prev && newlyPaid.length > 0) {
          const fresh = newlyPaid[0];
          setRecentlyPaid({ friendId: fresh.friend_id, name: fresh.name, until: Date.now() + 6000 });
          // Burst 14 confetti particles
          const burst = Array.from({ length: 14 }, (_, i) => ({
            id: `${Date.now()}-${i}`,
            left: `${Math.random() * 100}%`,
            delay: `${Math.random() * 300}ms`,
            color: ["#E83E8C", "#FF9F1C", "#8B5CF6", "#10B981", "#FFFFFF"][i % 5],
          }));
          setConfetti(prev => [...prev, ...burst]);
          setTimeout(() => setConfetti(p => p.filter(c => !burst.find(b => b.id === c.id))), 3500);
        }
        prevPaidRef.current = new Set((data.friends || []).filter(f => f.status === "paid").map(f => f.friend_id));
        return data;
      });
    } catch (err) {
      setPool(null);
    }
    setLoading(false);
    // setPool / setRecentlyPaid / setConfetti setters are stable; prevPaidRef is a
    // ref (not a reactive dep). Only poolId drives a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolId]);

  useEffect(() => {
    fetchPool();
    const iv = setInterval(fetchPool, 8000);
    return () => clearInterval(iv);
  }, [fetchPool]);

  const nudge = async () => {
    setNudging(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/party-vibes/split-pay/${poolId}/nudge`);
      alert(`Nudged ${data.nudged} friend(s) via WhatsApp`);
    } catch { alert("Nudge failed"); }
    setNudging(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#E83E8C]" size={32}/></div>;
  if (!pool) return <div className="min-h-screen flex items-center justify-center text-gray-500">Pool not found</div>;

  const pct = pool.progress_percent || 0;
  const isComplete = pool.status === "collected";

  return (
    <div className="min-h-screen bg-[#FFFAF5] relative overflow-hidden" data-testid="pool-tracker">
      {/* Confetti burst overlay */}
      {confetti.length > 0 && (
        <div className="absolute inset-0 pointer-events-none z-40" data-testid="pool-confetti">
          {confetti.map(c => (
            <span
              key={c.id}
              className="absolute top-0 w-2 h-3 rounded-sm animate-confetti-fall"
              style={{ left: c.left, backgroundColor: c.color, animationDelay: c.delay }}
            />
          ))}
        </div>
      )}
      <div className="bg-gradient-to-br from-[#1E1B4B] to-[#E83E8C] px-5 py-8 text-white">
        <button onClick={() => navigate("/")} className="text-white/70 text-xs mb-3 flex items-center gap-1"><Home size={12}/> DoozyGo</button>
        <p className="text-xs opacity-80 uppercase tracking-wider">{pool.occasion} · {pool.city}</p>
        <h1 className="text-3xl font-black font-['Outfit'] mt-1">{pool.host_name}'s Party Pool</h1>

        {/* Progress bar */}
        <div className="mt-5">
          <div className="flex justify-between items-baseline mb-2 text-sm">
            <span>₹{pool.collected_amount?.toLocaleString() || 0} collected</span>
            <span className="opacity-70">of ₹{pool.total_amount?.toLocaleString()}</span>
          </div>
          <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#FF9F1C] to-white transition-all duration-500" style={{ width: `${pct}%` }}/>
          </div>
          <p className="text-xs opacity-80 mt-2">{pool.progress_paid_count}/{pool.progress_total_count} friends paid</p>
        </div>
      </div>

      <div className="max-w-xl mx-auto p-5 space-y-4">
        {isComplete && pool.booking_id && (
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 text-white text-center" data-testid="pool-complete">
            <PartyPopper size={32} className="mx-auto mb-2"/>
            <h3 className="text-xl font-black">Pool Complete! 🎉</h3>
            <p className="text-sm opacity-90 mt-1">Booking confirmed. DoozyGo decor team is on it!</p>
            <button onClick={() => navigate(`/track/${pool.booking_id}`)} className="mt-3 px-4 py-2 bg-white text-emerald-600 rounded-xl text-sm font-bold">
              Track Booking {pool.booking_id}
            </button>
          </div>
        )}

        {/* Deadline warning */}
        {!isComplete && pool.deadline_at && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-center gap-2">
            <Clock size={16} className="text-amber-600"/>
            <p className="text-xs text-amber-700">Deadline: <b>{new Date(pool.deadline_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</b> · booking stays pending until full amount is collected</p>
          </div>
        )}

        {/* Friends grid */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-wider text-gray-500 font-bold">Who's paying</h3>
            {!isComplete && (
              <button onClick={nudge} disabled={nudging} className="text-xs text-[#E83E8C] font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="nudge-btn">
                <Bell size={12}/> {nudging ? "Nudging…" : "Nudge pending"}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {(pool.friends || []).map((f) => {
              const payUrl = `${window.location.origin}/pool/${poolId}/pay/${f.friend_id}`;
              const justPaid = recentlyPaid?.friendId === f.friend_id && Date.now() < (recentlyPaid?.until || 0);
              return (
                <div
                  key={f.friend_id}
                  className={`relative flex items-center gap-3 py-2 border-b last:border-0 border-gray-100 rounded-lg transition-all ${
                    justPaid ? "bg-gradient-to-r from-green-50 via-pink-50 to-orange-50 -mx-2 px-2 scale-[1.02] shadow-sm" : ""
                  }`}
                  data-testid={`pool-friend-${f.friend_id}`}
                >
                  {/* Ripple ring on newly paid */}
                  {justPaid && (
                    <span className="absolute left-3 top-3.5 w-8 h-8 rounded-full bg-green-400/40 animate-ping pointer-events-none" />
                  )}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold relative z-10 ${f.status === "paid" ? "bg-green-500" : "bg-gray-300"}`}>
                    {f.status === "paid" ? <Check size={14}/> : (f.name?.[0] || "?")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1E1B4B] truncate flex items-center gap-1.5">
                      {f.name}
                      {justPaid && <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-bold animate-pulse">JUST PAID 🎉</span>}
                    </p>
                    <p className="text-[11px] text-gray-400">₹{f.share} · {f.status === "paid" ? "✅ Paid" : "⏳ Pending"}</p>
                  </div>
                  {f.status !== "paid" && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(payUrl); setCopied(f.friend_id); setTimeout(() => setCopied(""), 1200); }}
                      className="text-xs text-gray-400 hover:text-[#E83E8C] flex items-center gap-1"
                      data-testid={`copy-pay-url-${f.friend_id}`}
                    >
                      {copied === f.friend_id ? <Check size={12}/> : <Copy size={12}/>} link
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Share pool + leaderboard peek */}
        {!isComplete && (
          <>
            <button
              onClick={() => {
                const shareUrl = `${window.location.origin}/pool/${poolId}`;
                if (navigator.share) navigator.share({ title: "DoozyGo® Party Pool", text: `Join our party pool for ${pool.occasion}!`, url: shareUrl }).catch(() => {});
                else { navigator.clipboard.writeText(shareUrl); alert("Pool link copied!"); }
              }}
              className="w-full py-3 rounded-xl bg-white border border-gray-200 text-[#1E1B4B] font-semibold flex items-center justify-center gap-2"
              data-testid="share-pool-btn"
            >
              <Share2 size={16}/> Share pool link
            </button>
            <button
              onClick={() => navigate("/leaderboard")}
              className="w-full py-2 text-xs text-gray-400 hover:text-[#E83E8C] flex items-center justify-center gap-1"
              data-testid="leaderboard-link"
            >
              🏆 See the Squad Leaderboard →
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   FriendPayPage — a single friend pays their share via Razorpay
════════════════════════════════════════════════════════════════ */
export const FriendPayPage = () => {
  const { poolId, friendId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_URL}/api/party-vibes/split-pay/${poolId}/friend/${friendId}`)
      .then(r => {
        setInfo(r.data);
        // Fetch the friend's gamification badge
        if (r.data?.friend_phone || r.data?.friend_name) {
          // Need phone to look up badge — fetch from pool
          return axios.get(`${API_URL}/api/party-vibes/split-pay/${poolId}`).then(pr => {
            const f = (pr.data.friends || []).find(x => x.friend_id === friendId);
            if (f?.phone) {
              return axios.get(`${API_URL}/api/party-vibes/leaderboard/badge?phone=${encodeURIComponent(f.phone)}`)
                .then(br => setBadge(br.data.badge)).catch(() => {});
            }
          });
        }
      })
      .catch(err => setError(err?.response?.data?.detail || "Pool not found"))
      .finally(() => setLoading(false));
  }, [poolId, friendId]);

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const pay = async () => {
    setError(""); setPaying(true);
    const ok = await loadRazorpay();
    if (!ok) { setError("Payment SDK failed"); setPaying(false); return; }

    const options = {
      key: info.key_id,
      amount: info.share * 100,
      currency: "INR",
      name: "DoozyGo® Party Pool",
      description: `${info.occasion} with ${info.host_name}`,
      order_id: info.order_id,
      prefill: { name: info.friend_name },
      theme: { color: "#E83E8C" },
      handler: async (resp) => {
        try {
          const { data } = await axios.post(`${API_URL}/api/party-vibes/split-pay/verify`, {
            pool_id: poolId, friend_id: friendId, ...resp
          });
          if (data.status === "paid" || data.status === "already_paid") {
            navigate(`/pool/${poolId}`);
          } else { setError("Verification failed"); }
        } catch (err) { setError(err?.response?.data?.detail || "Verify failed"); }
      },
      modal: { ondismiss: () => setPaying(false) }
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#E83E8C]" size={32}/></div>;
  if (error && !info) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center">
        <AlertTriangle className="mx-auto text-red-400 mb-3" size={40}/>
        <p className="text-sm text-gray-600">{error}</p>
      </div>
    </div>
  );
  if (!info) return null;

  if (info.status === "paid") {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-6 text-center max-w-sm w-full space-y-4" data-testid="friend-already-paid">
          <Check className="mx-auto text-green-500" size={48}/>
          <div>
            <h2 className="text-xl font-bold text-[#1E1B4B]">Already Paid ✅</h2>
            <p className="text-sm text-gray-500 mt-1">You've contributed ₹{info.share}. See you at the party! 🎉</p>
          </div>
          <button onClick={() => navigate(`/pool/${poolId}`)} className="w-full px-4 py-2.5 bg-[#E83E8C] text-white rounded-xl text-sm font-bold" data-testid="view-pool-status-btn">
            View Pool Status
          </button>

          {/* Viral acquisition loop: convert paying friend into a new host */}
          <div className="pt-4 mt-2 border-t border-gray-100">
            <div className="bg-gradient-to-br from-[#1E1B4B] to-[#E83E8C] rounded-2xl p-4 text-white text-left">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80 mb-1">
                <Sparkles size={12}/> Your turn
              </div>
              <p className="text-sm font-bold leading-snug mb-1">Host your own party,<br/>split with your squad 💸</p>
              <p className="text-[11px] opacity-80 mb-3">Get a Gen-Z AI plan tuned to your budget in 15 sec.</p>
              <button
                onClick={() => navigate(`/plan-with-friends?occasion=${encodeURIComponent(info.occasion || 'Birthday')}`)}
                className="w-full py-2 bg-white text-[#1E1B4B] rounded-xl text-xs font-bold"
                data-testid="viral-host-cta"
              >
                Plan My Party →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFAF5] via-white to-pink-50" data-testid="friend-pay-page">
      <div className="bg-gradient-to-br from-[#1E1B4B] to-[#E83E8C] px-5 py-8 text-white">
        <div className="flex items-center gap-2 mb-2"><Sparkles size={16}/><span className="text-xs uppercase tracking-widest opacity-80">Party Pool Invite</span></div>
        <h1 className="text-2xl font-black font-['Outfit']">Hey {info.friend_name?.split(" ")[0]}! 👋</h1>
        {badge?.tier && (
          <div className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20" data-testid="friend-badge">
            <span className="text-sm">{badge.emoji}</span>
            <span className="text-[11px] font-bold tracking-wide">{badge.label}</span>
            {badge.desc && <span className="text-[10px] opacity-70 ml-1">· {badge.desc}</span>}
          </div>
        )}
        <p className="text-sm opacity-90 mt-2">{info.host_name} invited you to split the vibe for their <b>{info.occasion}</b>.</p>
      </div>

      <div className="max-w-md mx-auto p-5 -mt-4 relative z-10 space-y-4">
        <div className="bg-white rounded-2xl p-6 shadow-xl text-center">
          <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Your share</p>
          <p className="text-5xl font-black text-[#E83E8C] my-2">₹{info.share?.toLocaleString()}</p>
          {info.event_date && <p className="text-xs text-gray-500">for party on {info.event_date}</p>}

          {/* Progress visualizer */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-[11px] text-gray-500 mb-1.5">
              <span>₹{info.collected_amount?.toLocaleString() || 0} collected</span>
              <span>of ₹{info.total_amount?.toLocaleString()}</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#E83E8C] to-orange-500" style={{ width: `${info.progress_percent}%` }}/>
            </div>
          </div>

          {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

          <div className="mt-5">
            <PaymentTrustBadge variant="compact" />
          </div>

          <button onClick={pay} disabled={paying} className="mt-3 w-full py-4 rounded-xl bg-gradient-to-r from-[#E83E8C] to-orange-500 text-white font-black text-base shadow-lg disabled:opacity-50" data-testid="friend-pay-btn">
            {paying ? <Loader2 className="animate-spin mx-auto" size={18}/> : `Pay ₹${info.share} via UPI/Card`}
          </button>

          <p className="text-[10px] text-gray-400 mt-3">Booking auto-confirms when pool hits 100%</p>
        </div>

        <button onClick={() => navigate(`/pool/${poolId}`)} className="w-full text-xs text-gray-400 hover:text-[#E83E8C]">
          See who else is paying →
        </button>
      </div>
    </div>
  );
};
